import type Phaser from 'phaser';
import i18next from 'i18next';
import { GET_URL, QR_IMAGE, dismissQrPromo, shouldShowQrPromo } from '@/game/qrPromo';

/**
 * The small "get the app" QR card in the bottom-left corner of the web build, on desktop only.
 * The rules — who sees it, which code, and when — all live in src/game/qrPromo.ts.
 *
 * **Not a Phaser DOM element.** Every other HTML control in this game is one, because they belong
 * to a scene and are positioned in game space. This one belongs to the page: it has to survive
 * Menu → Round → Summary without being rebuilt, it must sit in a screen corner rather than at
 * some coordinate inside the 1280×720 play field (which is letterboxed on a desktop window, so a
 * "corner" in game space is not the corner of the screen), and it must respect the safe-area
 * insets the canvas is already inset by. So it is appended to `document.body` and positioned in
 * CSS, and it never touches Phaser's input plumbing — no `sealFromPhaser` needed, because it is
 * not inside the DOM container the game reads pointer events through.
 *
 * That also means `transform` is ours to animate, unlike the menu's pills where Phaser owns it —
 * hence the slide-in in the stylesheet.
 *
 * It gets out of the way whenever something else owns the screen, and always in CSS rather than by
 * being torn down, so no state can drift out of sync with what is actually showing:
 *  - a game is being played — decided by scene, see QUIET_SCENES below, with `body.in-game` as a
 *    second lock on the case that matters most;
 *  - `body.overlay-open` — a picker, the scoreboard or the privacy policy is up (src/ui/overlay.ts);
 *  - `body.promo-card-open` — a promo card is up (promoCard.ts). After four stars the buy card
 *    makes the same offer at full size, and two asks on one screen is one too many.
 */

/** Hidden state. A class and not an inline style: inline `display` would beat the rules above. */
const OFF_CLASS = 'qr-promo--off';

/**
 * Built at most once per page load and then shown and hidden in place.
 *
 * Rebuilding it on every scene change would be the obvious alternative and is a trap: each build
 * subscribes to i18next's `languageChanged`, and an element that is thrown away takes no listener
 * with it. A player moving between the Menu and a round a dozen times would leave a dozen live
 * label handlers behind, all writing to detached nodes. One element, one subscription.
 */
let root: HTMLElement | null = null;

function build(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'qr-promo';

  const link = document.createElement('a');
  link.className = 'qr-promo__link';
  // The same URL the code encodes, so a desktop visitor who clicks instead of scanning lands
  // where the scan would have — on the store chooser, which on a desktop honestly shows both.
  //
  // No `trackStoreClick` here, unlike every badge: that event names a store, and this card does
  // not know one. The destination is decided a device later, by the phone (see GET_URL), so
  // anything we logged at this click would be a guess dressed up as a measurement.
  link.href = GET_URL;
  link.target = '_blank';
  link.rel = 'noopener';

  const img = document.createElement('img');
  img.className = 'qr-promo__code';
  img.src = QR_IMAGE;
  img.alt = '';   // decorative: the text beside it says what this is, and a QR has no readable alt
  link.append(img);

  const text = document.createElement('span');
  text.className = 'qr-promo__text';
  const title = document.createElement('span');
  title.className = 'qr-promo__title';
  const hint = document.createElement('span');
  hint.className = 'qr-promo__hint';
  text.append(title, hint);
  link.append(text);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'qr-promo__close';
  close.textContent = '×';
  close.addEventListener('click', () => {
    dismissQrPromo();
    refreshQrPromo();
  });

  el.append(link, close);

  // Filled here and again on every language change, so a player who switches language on the Menu
  // does not leave a German card standing over a French game.
  const label = (): void => {
    title.textContent = i18next.t('qr.title');
    hint.textContent = i18next.t('qr.scan');
    close.setAttribute('aria-label', i18next.t('qr.close'));
    link.setAttribute('aria-label', `${i18next.t('qr.title')} — ${i18next.t('qr.scan')}`);
  };
  label();
  i18next.on('languageChanged', label);

  return el;
}

/**
 * The screens the card is allowed to share, and the reason this module knows about scenes at all.
 *
 * Everything not listed here is a game in progress — the board, Survival, and every minigame
 * (Pairs, Maze, Word Wagon, Recipe, Labyrinth…) — and an ad has no business on any of them. Naming
 * the three calm screens rather than the dozen busy ones is both shorter and safer: a minigame
 * added next year is hidden by default, which is the direction a mistake should fall.
 *
 * `Boot` and `Preload` are absent on purpose too: the first thing a visitor sees should be the
 * game loading, not a pitch over a progress bar.
 */
const QUIET_SCENES = ['Menu', 'Summary', 'Celebration'] as const;

/**
 * Set while the Menu is the screen underneath, so the CSS can lift the card clear of it.
 *
 * The Menu is the one quiet screen with something already in this corner: the mute glyph and the
 * privacy link, which the Families track requires to stay reachable (MenuScene.create). Lifting
 * the card is the whole fix — see `.qr-promo` in index.html for the measurement.
 */
const MENU_CLASS = 'qr-menu';

/** Whether the screen currently on top is one the card may share. See QUIET_SCENES. */
let sceneAllows = false;

/**
 * Show or hide the overlay to match the current rules. Safe to call as often as you like — it
 * builds at most one element and does nothing when the answer has not changed.
 */
export function refreshQrPromo(): void {
  if (!sceneAllows || !shouldShowQrPromo()) {
    root?.classList.add(OFF_CLASS);
    return;
  }

  if (!root) {
    root = build();
    document.body.append(root);
  }
  root.classList.remove(OFF_CLASS);
}

/**
 * Wire the overlay to the game's screens. Called once, from main.ts, with the Phaser game.
 *
 * Every scene is listened to from here rather than each one calling in: a scene should not have to
 * know that a store ad exists, and a list of subscriptions in one file is far easier to keep honest
 * than a line added to (or forgotten in) eighteen `create` methods.
 *
 * The event names are string literals, never `Phaser.Scenes.Events.*` — `Phaser` is imported here
 * for its types only, so the transform drops the import; reaching for a runtime constant would put
 * it back, and Phaser touches `window` at import time, which breaks every node test that then
 * reaches this module. Same trap as promoCard.ts documents.
 */
export function bindQrPromoToScenes(game: Phaser.Game): void {
  const update = (): void => {
    document.body.classList.toggle(MENU_CLASS, game.scene.isActive('Menu'));
    sceneAllows = QUIET_SCENES.some((key) => game.scene.isActive(key));
    refreshQrPromo();
  };

  const attach = (): void => {
    for (const scene of game.scene.scenes) {
      // 'create' is the one that matters, and it has to be 'create': it is emitted after the
      // scene manager has set the scene's status to RUNNING, so `isActive()` finally agrees that
      // the screen we are being told about is the screen that is up. 'ready' fires a step too
      // early — the listener runs, sees no running scene, and the card never appears.
      for (const e of ['create', 'shutdown', 'sleep', 'wake']) scene.events.on(e, update);
    }
    update();
  };

  // `game.scene.scenes` is EMPTY while the Phaser.Game constructor is still running — the scene
  // configs sit in the manager's pending queue until it builds them, which it does on the game's
  // own 'ready'. Its listener for that is registered inside the constructor, so it runs before
  // ours and the list is populated by the time we get there. The length check is belt and braces
  // for a caller that already has a booted game.
  if (game.scene.scenes.length > 0) attach();
  else game.events.once('ready', attach);
}
